import _extends from "@babel/runtime/helpers/extends";
import React from "react";
import MuiButton from "@mui/material/Button";
import MuiIconButton from "@mui/material/IconButton";
import classNames from "classnames";
import useStyles from "./styles";
import MuiFab from "@mui/material/Fab";
import Tooltip from "../Tooltip";
import Loader from "../Loader";
import { Transition } from "react-transition-group";
export const Button = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    children,
    className,
    component,
    color = "default",
    disabled,
    onClick,
    size,
    type = "default",
    variant = "contained",
    openInNewWindow,
    startIcon,
    endIcon,
    title,
    TooltipProps = {
      placement: "bottom"
    },
    stopPropagation,
    isLoading,
    target,
    rel = "noopener noreferrer",
    ...otherProps
  } = props;
  const classes = useStyles(props);
  const renderedOnClick = stopPropagation ? event => {
    event.stopPropagation();
    if (onClick) onClick(event);
  } : onClick;
  const touchRippleProps = {
    ...otherProps.TouchRippleProps,
    classes: {
      ...otherProps.TouchRippleProps?.classes,
      root: classNames("prmbl-touchRipple", otherProps.TouchRippleProps?.classes?.root)
    }
  };
  const loadingOverlay = /*#__PURE__*/React.createElement(Transition, {
    in: true,
    timeout: 500
  }, /*#__PURE__*/React.createElement(Loader, {
    size: 24
  }));
  const shouldFixTooltip = (isLoading || disabled) && title;
  const buttonProps = {
    color,
    // The tooltip doesn't work with a button component that is disabled, so render a span instead
    component: shouldFixTooltip ? "span" : component,
    className: classNames("prmbl-button", className, {
      [`prmbl-button--${color}`]: color,
      [classes.sizeSmall]: size === "small",
      [classes.sizeLarge]: size === "large",
      [classes.iconButtonSizeSmall]: size === "small" && type === "icon",
      [classes.iconButtonSizeLarge]: size === "large" && type === "icon",
      [classes.button]: type !== "icon" && type !== "fab",
      [classes.iconButton]: type === "icon",
      "prmbl-iconBtn": type === "icon",
      [classes.fab]: type === "fab",
      "prmbl-fabBtn": type === "fab",
      [classes.tooltipFix]: shouldFixTooltip
    }),
    disabled: isLoading || disabled,
    target: openInNewWindow ? "_blank" : target,
    rel,
    onClick: isLoading || disabled ? undefined : renderedOnClick,
    size,
    variant,
    ref,
    ...otherProps,
    TouchRippleProps: touchRippleProps
  };
  const buttonChildren = /*#__PURE__*/React.createElement(React.Fragment, null, isLoading && startIcon && /*#__PURE__*/React.createElement("span", {
    className: classes.startIcon
  }, loadingOverlay), !isLoading && startIcon && /*#__PURE__*/React.createElement("span", {
    className: classes.startIcon
  }, startIcon), isLoading && !startIcon && !endIcon && /*#__PURE__*/React.createElement("span", {
    className: classes.mainLoadingButton
  }, loadingOverlay), children, isLoading && endIcon && /*#__PURE__*/React.createElement("span", {
    className: classes.endIcon
  }, loadingOverlay), !isLoading && endIcon && /*#__PURE__*/React.createElement("span", {
    className: classes.endIcon
  }, endIcon));
  const renderedButton = type === "icon" ? /*#__PURE__*/React.createElement(MuiIconButton, _extends({}, buttonProps, {
    size: size === "large" ? undefined : size
  }), buttonChildren) : type === "fab" ? /*#__PURE__*/React.createElement(MuiFab, _extends({}, buttonProps, {
    variant: variant === "text" || variant === "outlined" || variant === "contained" ? undefined : variant
  }), buttonChildren) : /*#__PURE__*/React.createElement(MuiButton, buttonProps, buttonChildren);
  return title ? /*#__PURE__*/React.createElement(Tooltip, _extends({
    title: title,
    placement: "bottom"
  }, TooltipProps), renderedButton) : renderedButton;
});
Button.displayName = "Button";
export default Button;